<template>
  <div class="container">
    <div class="profileidentvolunteerformcommon">
      <div class="row">
        <div class="col-12">
          <h2>{{ addEditTitle }}</h2>
        </div>
      </div>
      <div class="row">
        <div class="col-12 text-center py-2">
          <validation :id="'validationTarget' + _uid"
                      ref="validationtarget"
                      :validation-errors="validationErrors"
          />
        </div>
      </div>
      <div class="row feature-panel">
        <div class="col-12">
          <VolunteerProfileCommon :volunteer="volunteer" @input="onVolunteerChanged" />
        </div>
      </div>
      <div class="row feature-panel buttons">
        <div class="col-6 cancel">
          <SprButton type="button"
                     :size="1"
                     :title="$t('volunteerForm.cancel')"
                     click-event="onCancel"
                     @onCancel="cancel"
          />
        </div>
        <div class="col-6 save">
          <SprButton type="button"
                     :size="1"
                     :title="$t('volunteerForm.save')"
                     click-event="onSave"
                     @onSave="save"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue'
  import { LocaleMessage } from 'vue-i18n'
  import SprButton from '@/components/SPRButton.vue'
  import VolunteerProfileCommon from '@/components/friends/VolunteerProfileCommon.vue'
  import validation from '../SPRValidationError.vue'
  import Alerts from '@/mixins/alerts.js'
  import { ErrorBag, FieldError } from 'vee-validate'
  import { FriendVolunteerDto, CustomerState, PreferredGenders } from '@/types/index'

  // the data object
  export interface TemplateComponentData {
    volunteer: FriendVolunteerDto,
    isSavedOrCancelled: boolean
  }

  // This is the component for a user to add and update their own Friend profile.
  export default Vue.extend({
    name: 'ProfileIdentVolunteerFormCommon',
    components: { validation, VolunteerProfileCommon, SprButton },
    mixins: [Alerts],
    props: {
    },
    data (): TemplateComponentData {
      return {
        volunteer: {} as FriendVolunteerDto,
        isSavedOrCancelled: false
      }
    },
    computed: {
      addEditTitle (): string | LocaleMessage {
        return this.$t('volunteerForm.updateTitle')
      },
      validationErrors (): Array<Array<string>> {
        var errs: ErrorBag = this.errors ? this.errors : []
        if (errs.items.length) {
          return errs.items.map((err: FieldError) => {
            return [`error_${err.field}`, err.rule ? err.rule : '', err.msg ? err.msg : '']
          })
        }
        return []
      }
    },
    mounted (): void {
      this.volunteer = JSON.parse(JSON.stringify(this.$store.state.profile.friendInfo))
    },
    provide (): Record<string, any> {
      return {
        $validator: this.$validator // allow child components to have validator injected
      }
    },
    beforeRouteLeave (to, from, next) {
      if (this.changeDetected() && !this.isSavedOrCancelled) {
        var text = this.$t('profile.unsavedChanges').toString()
        const answer = window.confirm(text)
        if (!answer) {
          return next(false)
        }
      }
      this.isSavedOrCancelled = false
      return next()
    },
    methods: {
      changeDetected (): boolean {
        return JSON.stringify(this.$store.state.profile.friendInfo) !== JSON.stringify(this.volunteer)
      },
      onVolunteerChanged (newVolunteer: FriendVolunteerDto): void {
        // only change the basic details, dont overwrite the net and standard profiles.
        const origStandardProfile = { ...this.volunteer.standardProfile }
        const origNetProfile = { ...this.volunteer.netProfile }
        this.volunteer = newVolunteer
        this.volunteer.standardProfile = origStandardProfile
        this.volunteer.netProfile = origNetProfile
      },
      cancel (): void {
        this.isSavedOrCancelled = true
        this.$router.push('/profile')
      },
      save (): void {
        this.isSavedOrCancelled = true
        this.$validator.validateAll().then(result => {
          // check any child controls have not added errors
          if (!result || (this.validationErrors && this.validationErrors.length)) {
            var target = this.$refs['validationtarget']
            if (target) {
              window.scrollTo({
                // @ts-ignore
                top: target.$el.parentElement.clientHeight,
                left: 0,
                behavior: 'smooth'
              })
            }
            return
          }

          const data = this.volunteer
          data.profileId = this.$store.state.profile.profileId
          this.updateFriendProfile(data)
        })
      },
      updateFriendProfile (data: FriendVolunteerDto): void {
        const updateUrl = this.$store.state.apiBases.friendservice + this.$store.state.apiEndpoints.volunteeridentupdate[0]
        this.$http.put(updateUrl, data).then((result: any) => {
          // @ts-ignore (doesnt always pick up mixins)
          this.fetchProfile((response: any) => {
            this.showAlertSuccess(this.$t('userAdmin.saveSuccess'))

            this.$store.commit('setProfile', response.body)
            this.$router.push('/profile')
          })
        }, (err: Response | any) => {
          this.displayError(err)
        })
      },
      displayError (err: Response | any): void {
        if (Array.isArray(err.body)) {
          err.body.forEach(x => {
            // @ts-ignore
            this.showAlertError(x.errorMessage)
          })
        } else if (err.body.errorMessage) {
          // @ts-ignore
          this.showAlertError(err.errorMessage)
        } else if (err.bodyText) {
          // @ts-ignore
          this.showAlertError(err.bodyText)
        } else {
          // @ts-ignore
          this.showAlertError(this.$t('common.fetchFailed'))
        }
      }
    }
  })
</script>

<style lang="scss">
  @import "../../assets/scss/_variables.scss";
  .profileidentvolunteerformcommon {
    .feature-panel {
      background-color: $brand-white;
      margin-bottom: 30px;
      padding-top: 15px;

      &.buttons {
        margin-top: 30px;
        padding-top: 15px;
        padding-bottom: 15px;
        .cancel {
          text-align: left;
        }
        .save {
          text-align: right;
        }
      }
    }

    .profilehead {
      margin-top: 25px;
      margin-bottom: 0;
      padding-top: 10px;
      padding-bottom: 10px;
      background-color: $brand-white;
    }

    .subheader {
      margin-top: 15px;
      margin-bottom: 15px;
      display: block;
    }

    .profile-button {
      margin-top: 20px;
      display: flex;
      justify-content: center;
    }

    .fa-exclamation-triangle {
      color: $brand-red;
    }
  }
</style>
